import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { parseEuroToCents, type Lease, type LeaseTermsUpdate } from '@vuokramani/shared';
import { pageStyles as s } from '../../components/page.tsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  Notice,
  SelectField,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { useUpdateLeaseTerms } from '../../lib/tenancy.ts';

const euros = (cents: number | null, lang: string) =>
  cents === null ? '' : (cents / 100).toLocaleString(lang, { minimumFractionDigits: 0 });

/**
 * Voimassa olevan vuokrasuhteen ehtojen muutos. Vakuus ja alkamispäivä eivät muutu.
 * Perustelu on pakollinen ja tallentuu lokiin vanhan ja uuden arvon kanssa.
 */
export function LeaseTermsDialog({ lease, onClose }: { lease: Lease; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const update = useUpdateLeaseTerms(lease.propertyId, lease.id);
  const terminated = Boolean(lease.terminatedBy);
  const [rent, setRent] = useState(euros(lease.rentCents, i18n.language));
  const [dueDay, setDueDay] = useState(String(lease.dueDay));
  const [waterBilling, setWaterBilling] = useState(lease.waterBilling);
  const [water, setWater] = useState(euros(lease.waterCents, i18n.language));
  const [electricity, setElectricity] = useState(lease.electricityBilling);
  const [increase, setIncrease] = useState(lease.rentIncreaseTerms ?? '');
  const [fixedTerm, setFixedTerm] = useState(Boolean(lease.endDate));
  const [endDate, setEndDate] = useState(lease.endDate ?? '');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);

  const rentCents = parseEuroToCents(rent);
  const waterCents = waterBilling === 'per_person' ? parseEuroToCents(water) : null;
  const errors = {
    rent: rentCents === null || rentCents < 1 ? t('errors.amount') : undefined,
    water: waterBilling === 'per_person' && waterCents === null ? t('errors.amount') : undefined,
    endDate:
      !terminated && fixedTerm && (!endDate || endDate < lease.startDate)
        ? t(endDate ? 'errors.endBeforeStart' : 'errors.date')
        : undefined,
    reason: reason.trim().length < 3 ? t('errors.reason') : undefined,
  };
  const valid = !Object.values(errors).some(Boolean);
  const err = (k: keyof typeof errors) => (tried ? errors[k] : undefined);

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!valid) return;
    const body: LeaseTermsUpdate = {
      rentCents: rentCents!,
      dueDay: Number(dueDay),
      waterBilling,
      waterCents,
      electricityBilling: electricity,
      rentIncreaseTerms: increase.trim() || null,
      // Irtisanotun päättymispäivä muuttuu vain irtisanomisen kautta.
      endDate: terminated ? lease.endDate : fixedTerm ? endDate : null,
      reason: reason.trim(),
    };
    update.mutate(body, { onSuccess: onClose });
  }

  return (
    <Dialog open onClose={onClose} title={t('terms.title')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <Notice>{t('terms.intro')}</Notice>
        <div className={s.formRow}>
          <TextField
            label={t('newLease.rent')}
            inputMode="decimal"
            value={rent}
            onChange={(e) => setRent(e.target.value)}
            error={err('rent')}
          />
          <SelectField
            label={t('lease.dueDay')}
            value={dueDay}
            onChange={(e) => setDueDay(e.target.value)}
          >
            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
              <option key={d} value={d}>
                {t('lease.dueDayValue', { day: d })}
              </option>
            ))}
          </SelectField>
        </div>
        <div className={s.formRow}>
          <SelectField
            label={t('lease.waterLabel')}
            value={waterBilling}
            onChange={(e) => setWaterBilling(e.target.value as Lease['waterBilling'])}
          >
            <option value="per_person">{t('lease.water.per_person')}</option>
            <option value="metered">{t('lease.water.metered')}</option>
            <option value="included">{t('lease.water.included')}</option>
          </SelectField>
          {waterBilling === 'per_person' && (
            <TextField
              label={t('newLease.waterPerPerson')}
              inputMode="decimal"
              value={water}
              onChange={(e) => setWater(e.target.value)}
              error={err('water')}
            />
          )}
        </div>
        <SelectField
          label={t('lease.electricityLabel')}
          value={electricity}
          onChange={(e) => setElectricity(e.target.value as Lease['electricityBilling'])}
        >
          <option value="own_contract">{t('lease.electricity.own_contract')}</option>
          <option value="included">{t('lease.electricity.included')}</option>
        </SelectField>
        {!terminated && (
          <div className={s.formRow}>
            <SelectField
              label={t('lease.duration')}
              value={fixedTerm ? 'fixed' : 'indefinite'}
              onChange={(e) => setFixedTerm(e.target.value === 'fixed')}
            >
              <option value="indefinite">{t('lease.indefinite')}</option>
              <option value="fixed">{t('newLease.fixedTerm')}</option>
            </SelectField>
            {fixedTerm && (
              <TextField
                label={t('newLease.endDate')}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                error={err('endDate')}
              />
            )}
          </div>
        )}
        <TextArea
          label={t('newLease.increase')}
          rows={2}
          value={increase}
          onChange={(e) => setIncrease(e.target.value)}
        />
        <TextArea
          label={t('terms.reason')}
          hint={t('terms.reasonHint')}
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={err('reason')}
        />
        {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('terms.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
